import React, { useEffect, useState } from 'react';
import api from '../api/api';
import './pnl.css';

const PnlPage = () => {
    const [month, setMonth] = useState(new Date().getMonth() + 1);
    const [year, setYear] = useState(new Date().getFullYear());
    const [filterKey, setFilterKey] = useState('all');
    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(false);

    const monthOptions = [
        { value: 1, label: 'January' },
        { value: 2, label: 'February' },
        { value: 3, label: 'March' },
        { value: 4, label: 'April' },
        { value: 5, label: 'May' },
        { value: 6, label: 'June' },
        { value: 7, label: 'July' },
        { value: 8, label: 'August' },
        { value: 9, label: 'September' },
        { value: 10, label: 'October' },
        { value: 11, label: 'November' },
        { value: 12, label: 'December' }
    ];

    const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(Number(value) || 0);

    const getAggregateValue = (row) => {
        if (!report || !report.properties) return 0;

        if (row.key === 'occupancyPercent') {
            if (report.properties.length === 0) return 0;
            const total = report.properties.reduce((sum, p) => sum + (Number(row.values[p.id]) || 0), 0);
            return total / report.properties.length;
        }

        return report.properties.reduce((sum, p) => sum + (Number(row.values[p.id]) || 0), 0);
    };

    const rowOrder = {
        bookingCount: 1,
        occupancyPercent: 2,
        rentExpense: 3,
        wifiExpense: 4,
        suppliesExpense: 5,
        otherExpense: 6,
        pricingToolsExpense: 7,
        cleaningExpense: 8,
        waterExpense: 9,
        gasExpense: 10,
        energyExpense: 11,
        unexpectedOpExExpense: 12,
        totalExpense: 13,
        bookingRevenue: 14,
        net: 15
    };

    const orderedRows = report && report.rows
        ? [...report.rows].sort((a, b) => (rowOrder[a.key] || 999) - (rowOrder[b.key] || 999))
        : [];

    const getOccupancyBg = (value) => {
        const pct = (Number(value) || 0) * 100;
        if (pct < 30) return '#fde8e8';
        if (pct < 65) return '#fefde8';
        return '#e8f5e9';
    };

    const isSummaryRow = (rowKey) => ['totalExpense', 'bookingRevenue', 'net'].includes(rowKey);
    const isSummaryStartRow = (rowKey) => rowKey === 'totalExpense';
    const formatCellValue = (rowKey, value) => {
        if (rowKey === 'bookingCount') {
            return Number(value || 0).toLocaleString('en-US');
        }

        if (rowKey === 'occupancyPercent') {
            const percentageValue = (Number(value) || 0) * 100;
            return `${Math.round(percentageValue)}%`;
        }

        return formatCurrency(value);
    };

    const loadMatrix = async () => {
        setLoading(true);
        try {
            const selectedPartnerId = String(filterKey || '').startsWith('partner:')
                ? Number(String(filterKey).split(':')[1])
                : null;

            const scope = filterKey === 'owned' ? 'owned' : (selectedPartnerId ? 'partner' : 'all');
            const params = new URLSearchParams({
                month: String(month),
                year: String(year),
                scope
            });
            if (scope === 'partner' && Number.isInteger(selectedPartnerId) && selectedPartnerId > 0) {
                params.set('partnerUserId', String(selectedPartnerId));
            }

            const res = await api.get(`/report/pnl-matrix?${params.toString()}`);
            if (res && res.data) setReport(res.data);
        } catch (e) {
            console.error('failed to load pnl matrix', e);
            setReport(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadMatrix();
    }, []);

    const generate = async () => { loadMatrix(); };

    const loadButtonBaseStyle = {
        backgroundColor: '#15803d',
        color: '#ffffff',
        border: 'none',
        borderRadius: '8px',
        padding: '10px 14px',
        cursor: 'pointer',
        fontWeight: 600
    };

    return (
        <div className="pnl-page">
            <div className="pnl-toolbar">
                <div>
                    <h2>Monthly P&amp;L</h2>
                    <p className="pnl-toolbar-copy">Review portfolio performance across your properties and partner collaborations.</p>
                </div>
                <div className="pnl-filter-bar">
                    <label className="pnl-filter-field">
                        <span>Month</span>
                        <select value={month} onChange={e => setMonth(Number(e.target.value))}>
                    {monthOptions.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                        </select>
                    </label>
                    <label className="pnl-filter-field pnl-filter-year">
                        <span>Year</span>
                        <input type="number" value={year} onChange={e => setYear(e.target.value)} />
                    </label>
                    <label className="pnl-filter-field pnl-filter-scope">
                        <span>Portfolio Filter</span>
                        <select value={filterKey} onChange={(e) => setFilterKey(e.target.value)}>
                            <option value="all">All Hosted Properties</option>
                            <option value="owned">My Properties Only</option>
                            {(report?.partnerOptions || []).map((partner) => (
                                <option key={partner.id} value={`partner:${partner.id}`}>
                                    With {partner.display || partner.email}
                                </option>
                            ))}
                        </select>
                    </label>
                    <button
                        onClick={generate}
                        style={loadButtonBaseStyle}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#166534'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#15803d'; }}
                    >
                        Load Report
                    </button>
                </div>
            </div>

            <div className="pnl-filter-pills">
                <span className="pnl-pill">{monthOptions.find((entry) => entry.value === month)?.label || 'Month'} {year}</span>
                <span className="pnl-pill pnl-pill-accent">
                    {filterKey === 'all'
                        ? 'All Hosted Properties'
                        : filterKey === 'owned'
                            ? 'My Properties Only'
                            : `With ${(report?.partnerOptions || []).find((entry) => `partner:${entry.id}` === filterKey)?.display || 'Partner'}`}
                </span>
            </div>

            {loading && <p>Loading report...</p>}

            {!loading && report && report.properties && report.rows && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr>
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', padding: '8px' }}>Type</th>
                                {report.properties.map(p => (
                                    <th key={p.id} style={{ textAlign: 'left', borderBottom: '1px solid #ddd', borderLeft: '1px solid #d9dde3', padding: '8px' }}>{p.name}</th>
                                ))}
                                <th style={{ textAlign: 'left', borderBottom: '1px solid #ddd', borderLeft: '2px solid #adb5bd', padding: '8px' }}>Aggregate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {orderedRows.map((row) => (
                                <tr
                                    key={row.key}
                                    style={{
                                        backgroundColor: isSummaryRow(row.key) ? '#f8f9fa' : 'white'
                                    }}
                                >
                                    <td style={{ borderTop: isSummaryStartRow(row.key) ? '2px solid #adb5bd' : 'none', borderBottom: row.key === 'net' ? '2px solid #adb5bd' : '1px solid #eee', padding: '8px', fontWeight: isSummaryRow(row.key) ? 700 : 500 }}>{row.label}</td>
                                    {report.properties.map((p) => (
                                        <td
                                            key={`${row.key}-${p.id}`}
                                            style={{
                                                borderTop: isSummaryStartRow(row.key) ? '2px solid #adb5bd' : 'none',
                                                borderBottom: row.key === 'net' ? '2px solid #adb5bd' : '1px solid #eee',
                                                borderLeft: '1px solid #d9dde3',
                                                padding: '8px',
                                                color: row.key === 'net'
                                                    ? (Number(row.values[p.id]) < 0 ? '#c92a2a' : '#15803d')
                                                    : 'inherit',
                                                fontWeight: isSummaryRow(row.key) ? 700 : 400,
                                                backgroundColor: row.key === 'occupancyPercent' ? getOccupancyBg(row.values[p.id]) : 'inherit'
                                            }}
                                        >
                                            {formatCellValue(row.key, row.values[p.id])}
                                        </td>
                                    ))}
                                    <td
                                        style={{
                                            borderTop: isSummaryStartRow(row.key) ? '2px solid #adb5bd' : 'none',
                                            borderBottom: row.key === 'net' ? '2px solid #adb5bd' : '1px solid #eee',
                                            borderLeft: '2px solid #adb5bd',
                                            padding: '8px',
                                            color: row.key === 'net'
                                                ? (getAggregateValue(row) < 0 ? '#c92a2a' : '#15803d')
                                                : 'inherit',
                                            fontWeight: 700,
                                            backgroundColor: row.key === 'occupancyPercent' ? getOccupancyBg(getAggregateValue(row)) : 'inherit'
                                        }}
                                    >
                                        {formatCellValue(row.key, getAggregateValue(row))}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PnlPage;
